import { useNavigate, useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Discount } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import {
  emptyDiscountForm,
  fromDiscount,
  toDiscountCreate,
  toDiscountUpdate,
} from '../api/mappers';
import { DeleteDiscountDialog } from '../components/delete-discount-dialog';
import { DiscountKindBadge, DiscountStateBadge } from '../components/discount-badges';
import { DiscountCodesCard } from '../components/discount-codes-card';
import { DiscountForm } from '../components/discount-form';
import { DiscountUsagesCard } from '../components/discount-usages-card';
import { useDiscount } from '../hooks/use-discount';
import { useCreateDiscount, useUpdateDiscount } from '../hooks/use-discount-mutations';
import { useDiscountValue } from '../hooks/use-discount-value';
import { discountListSearchSchema } from '../schemas/discount-list-search';

const EDIT_ROUTE = '/_authed/_active/s/$storeId/discounts/$discountId' as const;

function DiscountCreate() {
  const { t } = useTranslation('discounts');
  const store = useStore();
  const navigate = useNavigate();
  const create = useCreateDiscount(store.id);

  return (
    <PageContent>
      <PageHeader
        title={t('create.title')}
        backTo={{ to: '/s/$storeId/discounts', params: { storeId: store.id } }}
        backLabel={t('title')}
      />
      <DiscountForm
        storeId={store.id}
        currency={store.defaultCurrency}
        mode="create"
        defaultValues={emptyDiscountForm()}
        isSubmitting={create.isPending}
        onSubmit={async (values) => {
          const created = await create.mutateAsync(toDiscountCreate(values));
          await navigate({
            to: '/s/$storeId/discounts/$discountId',
            params: { storeId: store.id, discountId: created.id },
            replace: true,
          });
        }}
      />
    </PageContent>
  );
}

function DangerZone({ storeId, discount }: { storeId: string; discount: Discount }) {
  const { t } = useTranslation('discounts');
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<Discount | null>(null);
  return (
    <FormSection title={t('danger.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">{t('danger.description')}</p>
      <Button
        type="button"
        variant="outline"
        className="text-destructive hover:text-destructive w-full"
        onClick={() => setDeleting(discount)}
      >
        <Trash2 aria-hidden />
        {t('actions.delete')}
      </Button>
      <DeleteDiscountDialog
        storeId={storeId}
        discount={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        onDeleted={() =>
          void navigate({
            to: '/s/$storeId/discounts',
            params: { storeId },
            search: discountListSearchSchema.parse({}),
          })
        }
      />
    </FormSection>
  );
}

function DiscountEdit() {
  const { t } = useTranslation('discounts');
  const format = useFormat();
  const store = useStore();
  const { can } = usePermissions();
  const { discountId } = useParams({ from: EDIT_ROUTE });
  const value = useDiscountValue();
  // Prefetched by the route loader, so this resolves from cache.
  const { data: discount } = useDiscount(store.id, discountId);
  const update = useUpdateDiscount(store.id);
  if (!discount) return null;

  return (
    <PageContent>
      <PageHeader
        title={discount.title}
        meta={
          <>
            <DiscountKindBadge kind={discount.kind} />
            <DiscountStateBadge discount={discount} />
          </>
        }
        description={
          <span title={format.utc(discount.updatedAt)}>
            {value(discount)}
            {' · '}
            {t('usage.used', { count: discount.usageCount })}
            {' · '}
            {t('updatedAt', { when: format.relative(discount.updatedAt) })}
          </span>
        }
        backTo={{ to: '/s/$storeId/discounts', params: { storeId: store.id } }}
        backLabel={t('title')}
      />
      <DiscountForm
        key={discount.updatedAt}
        storeId={store.id}
        currency={store.defaultCurrency}
        mode="edit"
        defaultValues={fromDiscount(discount)}
        isSubmitting={update.isPending}
        onSubmit={(values) =>
          update.mutateAsync({ id: discount.id, body: toDiscountUpdate(values) })
        }
        dangerZone={
          <Can permission="discounts.write">
            <DangerZone storeId={store.id} discount={discount} />
          </Can>
        }
      >
        {discount.kind === 'code' ? (
          <DiscountCodesCard
            storeId={store.id}
            discount={discount}
            canWrite={can('discounts.write')}
          />
        ) : null}
        <DiscountUsagesCard storeId={store.id} discountId={discount.id} />
      </DiscountForm>
    </PageContent>
  );
}

export function DiscountCreatePage() {
  return (
    <FeatureErrorBoundary name="discounts.create">
      <DiscountCreate />
    </FeatureErrorBoundary>
  );
}

export function DiscountEditPage() {
  return (
    <FeatureErrorBoundary name="discounts.edit">
      <DiscountEdit />
    </FeatureErrorBoundary>
  );
}
